import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import { playSlideSound } from '@gamiphy/loyalty-station-desktop/sounds';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { IntegrationsStore } from '@gamiphy/loyalty-station-desktop/store/integrations';
import { DiscountStore } from '@gamiphy/loyalty-station-desktop/store/reward/discount';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { Pages, Tabs, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { formatCash } from '@gamiphy/utils/currency';
import { formatNumber } from '@gamiphy/utils/number';
import {
  Box,
  Button,
  Grid,
  Theme,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React from 'react';
import { PopulatedDiscountCode } from '@coretava/api/core-service';
import Slider from './Slider';
import { getRedeemPercentage } from './utils';

const useStyles = makeStyles<Theme, { disabled: boolean | undefined }>((theme) => ({
  root: {
    width: '100%',
    borderRadius: 4,
    position: 'relative',
    minHeight: '23rem',
  },
  number: {
    fontWeight: 600,
    color: ({ disabled }) => (disabled ? '#cfcfcf' : ''),
  },
  box: {
    height: '5rem',
    minWidth: '92px',
    borderRadius: '6px',
    fontSize: '1.4rem',
    fontWeight: 500,
    border: ({ disabled }) => (`1px solid ${disabled ? '#cfcfcf' : '#909090'}`),
    textAlign: 'center',
  },
  equal: {
    fontSize: '1.6rem',
    fontWeight: 500,
    lineHeight: '1.8rem',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    textAlign: 'left',
    marginBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
  },
  btn: {
    padding: theme.spacing(1),
    borderRadius: 4,
    height: '4rem',
    '& span': {
      fontSize: '1.2rem',
      lineHeight: '1.5rem',
      fontWeight: 500,
    },
  },
  cta: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  ctaMessage: {
    fontWeight: 500,
    width: 'fit-content',
    textTransform: 'capitalize',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  sliderContainer: {
    paddingInline: '2.9rem',
    width: '100%',
  },
}));

type Props = {
  onRedeem: (discountCode: PopulatedDiscountCode) => Promise<void>,
  showApplicationError: boolean,
}

const SliderContainer: React.FC<Props> = observer(({
  onRedeem, showApplicationError,
}) => {
  const { user } = UserStore;
  const { appDiscount } = DiscountStore;
  const max = user?.points.gems ?? 0;

  const { theme } = ThemeStore;
  const { app } = AppStore;
  const { t } = useTranslation();
  const isLocked = React.useMemo(() => !user?.points.gems || ((user?.points.gems ?? 0) < DiscountStore.minGemsToRedeem), [user?.points.gems, DiscountStore.minGemsToRedeem]);
  const [disabled, setDisabled] = React.useState(false);
  const classes = useStyles({ disabled });
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [value, setValue] = React.useState<number>(Math.min(DiscountStore.maxGemsToRedeem, UserStore.user?.points.gems ?? 0));
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    setValue(Math.min(DiscountStore.maxGemsToRedeem, UserStore.user?.points.gems ?? 0));
  }, [DiscountStore.maxGemsToRedeem, UserStore.user?.points.gems]);

  const percentageRedeem = React.useMemo(() => getRedeemPercentage({ max, value }), [max, value]);

  const redeemDiscount = React.useCallback(async () => {
    if (UserStore.isRealUser) {
      try {
        setLoading(true);
        const discountCode = await DiscountStore.redeemDiscount(value);
        if (discountCode) {
          await onRedeem(discountCode);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setPopupOpen(true);
    }
    void IntegrationsStore.trackGTMEvent({ category: 'RedeemTab', action: 'redeemClicked' });
  }, [UserStore.isRealUser, value]);

  const alert = React.useMemo(() => {
    switch (true) {
      case DiscountStore.minGemsToRedeem && isLocked:
        return (
          <Alert severity="error">
            <Trans
              i18nKey="wallet.message.notEnough"
              defaults="Your {{, gems}} balance is not enough, the minimum redeeming {{, gems}} are {{ value , gems}}"
              values={{
                value: DiscountStore.minGemsToRedeem,
              }}
            />
          </Alert>
        );
      case value > DiscountStore.maxGemsToRedeem:
        return (
          <Alert severity="error">
            <Trans
              i18nKey="wallet.message.tooHigh"
              defaults="The maximum redeemable {{, gems}} are {{ value , gems}} per gift card"
              values={{
                value: DiscountStore.maxGemsToRedeem,
              }}
            />
          </Alert>
        );
      case value < DiscountStore.minGemsToRedeem:
        return (
          <Alert severity="error">
            <Trans
              i18nKey="wallet.message.tooLow"
              defaults="The minimum redeemable {{, gems}} are {{ value , gems}} per gift card"
              values={{
                value: DiscountStore.minGemsToRedeem,
              }}
            />
          </Alert>
        );
      case showApplicationError:
        return (
          <Alert severity="error">
            <Trans
              i18nKey="autoApply.error"
              defaults="Apologies, but the application process couldn't be completed at this time. Please attempt again later."
            />
          </Alert>
        );
      default:
        return (
          <Alert severity="info">
            <Trans
              i18nKey="pointsExpiry.message"
              defaults="Your {{, gems}} are valid for {{val, number}} from the date of issue."
              values={{
                val: Number(app.pointsExpiry.time),
                style: 'unit',
                unit: app.pointsExpiry.unit,
                unitDisplay: 'long',
              }}
            />
          </Alert>
        );
    }
  }, [value, DiscountStore.maxGemsToRedeem, DiscountStore.minGemsToRedeem, showApplicationError]);

  const ctaMessage = React.useMemo(() => {
    switch (true) {
      case isLocked:
        return (
          <Typography
            className={classes.ctaMessage}
            onClick={() => {
              UiStore.setTab(Tabs.Missions);
            }}
          >
            <Trans
              i18nKey="wallet.cta.getMore"
              defaults="Get more {{, gems}}"
              values={{}}
            />
          </Typography>
        );
      case value > DiscountStore.maxGemsToRedeem:
        return (
          <Typography
            className={classes.ctaMessage}
            onClick={() => setValue(DiscountStore.maxGemsToRedeem ?? max)}
          >
            <Trans
              i18nKey="wallet.cta.redeemMax"
              defaults="Redeem the maximum"
            />
          </Typography>
        );
      case value < DiscountStore.minGemsToRedeem:
        return (
          <Typography
            className={classes.ctaMessage}
            onClick={() => {
              setValue(DiscountStore.minGemsToRedeem ?? 0);
            }}
          >
            <Trans
              i18nKey="wallet.cta.redeemMin"
              defaults="Redeem the minimum"
            />
          </Typography>
        );
      default:
        return undefined;
    }
  }, [value, DiscountStore.maxGemsToRedeem, DiscountStore.minGemsToRedeem]);

  React.useEffect(() => {
    if (appDiscount && user) {
      setDisabled(!value || ((value < DiscountStore.minGemsToRedeem) || user.points.gems < value || (value > DiscountStore.maxGemsToRedeem)));
    }
  }, [value]);

  return !(app && appDiscount && theme && user) ? null : (
    <Box className={classes.root}>
      <Grid container direction="column" rowSpacing={2}>
        <Grid container justifyContent="flex-end" item className={classes.sliderContainer}>
          <Slider
            min={0}
            max={max}
            value={value}
            percentageRedeem={percentageRedeem}
            disabled={disabled}
            locked={isLocked}
            onChange={(v: number) => {
              setValue(v);
              void playSlideSound();
            }}
          />
        </Grid>
        <Grid item container alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={5}>
            <Typography variant="h5" className={classes.title}>{t('wallet.points', '{{, gems}}')}</Typography>
            <Grid container alignItems="center" justifyContent="center" className={classes.box}>
              <Typography variant="h4" className={classes.number}>
                {formatNumber(value, { notation: 'standard' })}
              </Typography>
            </Grid>
          </Grid>
          <Grid xs={12} sm={1} container alignItems="center" justifyContent="center" item>
            <Typography className={classes.equal}> = </Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Grid item container alignItems="center" justifyContent="center" className={classes.box}>
              <Typography variant="h4" className={classes.number}>
                {formatCash(value * appDiscount.value)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            onClick={redeemDiscount}
            disabled={disabled || loading}
            fullWidth
          >
            {
              DiscountStore.isAutoApply
                ? (
                  t('redeem.applyDiscountCode', 'Apply {{, discountType}}')
                )
                : (
                  t('redeem.redeemDiscountCode', 'Redeem {{, discountType}}')
                )
            }
          </Button>
        </Grid>
        {
          (DiscountStore.discounts?.length ?? 0) > 0 && (
            <Grid item>
              <Button
                fullWidth
                color="secondary"
                variant="text"
                onClick={() => UiStore.setPage(Pages.redeemHistory)}
              >
                <Trans
                  i18nKey="showRedeemHistory"
                  defaults="Show Redeem History"
                />
              </Button>
            </Grid>
          )
        }
        <UnavowedPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
        />
        <Grid item>
          {alert}
        </Grid>
        {ctaMessage && (
          <Grid container item justifyContent="center" className={classes.cta}>
            {ctaMessage}
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

export default SliderContainer;
